import { useState, useEffect } from 'react';
import { weekDays } from '@utils/constants';
import { Button, Checkbox, Switch, Input } from '@plick/ui';
import ModalComponent from '@components/interface/ModalComponent';
import { IconCopy, IconPlus, IconTrash, IconHelpCircleFilled } from '@tabler/icons-react';
import { plickTheme } from '@theme/index';
import { ScheduleItem, ScheduleManagerProps } from './ScheduleManager.types';
import { IconChevronRight } from '@tabler/icons-react';
import VideoTooltip from '@pages/Menu/Products/components/VideoTooltip';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

const ScheduleManager = ({
  schedules,
  onChange,
  autoSave = false,
  showSaveButton = false,
  onSave,
  readOnly = false,
  customTitle = 'Horarios de atención:',
}: ScheduleManagerProps) => {
  const { trackEvent } = useTracking();
  const [localSchedules, setLocalSchedules] = useState<ScheduleItem[]>(schedules || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const handleScheduleHelpClick = () => {
    trackEvent(TRACK_EVENT_NAMES.VideoTutorialTooltipClicked, {
      videoUrl: 'https://youtu.be/gLrrswGfRq0?si=Ffv_6iXsxU6s79Md',
      tutorialType: 'schedule_management',
    });
  };

  // Actualiza el estado local cuando cambian los schedules externos
  useEffect(() => {
    setLocalSchedules(schedules || []);
  }, [schedules]);

  // Función para aplicar cambios locales y posiblemente guardarlos
  const applyChanges = (newSchedules: ScheduleItem[]) => {
    setLocalSchedules(newSchedules);
    setHasChanges(true);

    if (autoSave) {
      onChange(newSchedules);
      setHasChanges(false);
    }
  };

  // Inicializa horarios por defecto si no existen
  useEffect(() => {
    if (!localSchedules || localSchedules.length === 0) {
      const defaultSchedules: ScheduleItem[] = [
        { dia_semana: '0', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '1', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '2', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '3', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '4', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '5', horario_apertura: '08:15', horario_cierre: '18:30' },
        { dia_semana: '6', horario_apertura: '08:15', horario_cierre: '13:30' },
      ];
      applyChanges(defaultSchedules);
    }
  }, []);

  // Maneja la copia de horarios entre días
  const handleCopySchedule = () => {
    if (selectedDay && localSchedules) {
      const sourceTurns = localSchedules.filter((item) => item.dia_semana === selectedDay);
      const updatedSchedules = localSchedules.filter((item) => !selectedDays.includes(item.dia_semana));
      selectedDays.forEach((day) => {
        sourceTurns.forEach((turn) => {
          updatedSchedules.push({ ...turn, dia_semana: day });
        });
      });
      applyChanges(updatedSchedules);
    }
    setIsModalOpen(false);
    setSelectedDays([]);
  };

  // Maneja el guardado explícito
  const handleSave = () => {
    onChange(localSchedules);
    setHasChanges(false);
    if (onSave) onSave();
  };

  if (!localSchedules) return null;

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold">{customTitle}</h3>
        <VideoTooltip
          videoUrl="https://youtu.be/gLrrswGfRq0?si=Ffv_6iXsxU6s79Md"
          title="Tutorial: Gestión de horarios"
          description="Aprende cómo configurar los horarios de apertura y cierre de tu negocio por días"
          placement="top"
          trigger="click"
          onShow={handleScheduleHelpClick}
        >
          <Button.Icon
            bgColor={plickTheme.colors.light}
            borderColor={plickTheme.colors.light}
            color={plickTheme.colors.primaryDark}
            icon={<IconHelpCircleFilled width={16} height={16} />}
            onClick={handleScheduleHelpClick}
          />
        </VideoTooltip>
      </div>

      {showSaveButton ? (
        <div className="w-full my-6 flex ">
          <Button.Primary
            text="GUARDAR CAMBIOS"
            onClick={handleSave}
            disabled={!hasChanges || readOnly}
            rightIcon={<IconChevronRight />}
          />
        </div>
      ) : null}

      <div className="w-full">
        {weekDays.map((day) => {
          const turns = localSchedules?.filter((item) => item.dia_semana === day.value);
          const hasTurns = Boolean(turns?.length);

          return (
            <div
              key={day.value}
              className="flex w-full flex-col gap-2 border-b-2 py-2 last:mb-0 sm:flex-row sm:gap-6 sm:px-0"
            >
              <div className="flex h-[36px] items-center w-28 sm:w-32 md:w-40 flex-shrink-0">
                <Switch
                  name={`day_switch_${day.value}`}
                  size="small"
                  isChecked={hasTurns}
                  disabled={readOnly}
                  onChange={(checked) =>
                    applyChanges(
                      checked
                        ? [
                            ...localSchedules,
                            { dia_semana: day.value, horario_apertura: '08:15', horario_cierre: '18:30' },
                          ]
                        : localSchedules.filter((item) => item.dia_semana !== day.value),
                    )
                  }
                />
                <span className="ml-2 text-gray-800 font-medium">{day.label}</span>
              </div>
              {hasTurns && (
                <div className="flex sm:gap-2">
                  <div className="flex flex-col gap-2">
                    {turns.map((turn, idx) => (
                      <div key={`turn_${day.value}_${idx}`} className="flex gap-1 last:mb-0 sm:gap-2">
                        <div className="flex flex-row gap-2 sm:gap-3">
                          <Input.Hours
                            value={turn.horario_apertura}
                            disabled={readOnly}
                            onChange={(value) => {
                              const updatedTurn = { ...turn, horario_apertura: value };
                              const indexToUpdate = localSchedules.indexOf(turn);
                              applyChanges(localSchedules.map((item, i) => (i === indexToUpdate ? updatedTurn : item)));
                            }}
                            label="Horario de apertura"
                            name={`hora_apertura_${day.value}_${idx}`}
                          />

                          <Input.Hours
                            value={turn.horario_cierre}
                            disabled={readOnly}
                            onChange={(value) => {
                              const updatedTurn = { ...turn, horario_cierre: value };
                              const indexToUpdate = localSchedules.indexOf(turn);
                              applyChanges(localSchedules.map((item, i) => (i === indexToUpdate ? updatedTurn : item)));
                            }}
                            label="Horario de cierre"
                            name={`hora_cierre_${day.value}_${idx}`}
                          />
                        </div>

                        {!readOnly && (
                          <div className="flex mt-2 sm:mt-0 sm:ml-2 self-end">
                            {idx === 0 ? (
                              <>
                                <Button.Icon
                                  icon={<IconPlus color={plickTheme.colors.light} />}
                                  onClick={() => {
                                    applyChanges([
                                      ...localSchedules,
                                      { dia_semana: day.value, horario_apertura: '08:15', horario_cierre: '18:30' },
                                    ]);
                                  }}
                                  color="primary"
                                />
                                <div className="ml-1">
                                  <Button.Icon
                                    bgColor="rgba(0, 0, 0, 0)"
                                    color="gray"
                                    icon={<IconCopy />}
                                    lightHoverColor="rgba(140, 146, 170, 1)"
                                    onClick={() => {
                                      setSelectedDay(day.value);
                                      setIsModalOpen(true);
                                    }}
                                  />
                                </div>
                              </>
                            ) : (
                              <Button.Icon
                                bgColor="rgba(0, 0, 0, 0)"
                                color="gray"
                                icon={<IconTrash size={18} stroke={2} color="#F91515" />}
                                lightHoverColor="rgba(249, 21, 21, 0.1)"
                                onClick={() => {
                                  const indexToRemove = localSchedules.indexOf(turn);
                                  applyChanges(localSchedules.filter((_, i) => i !== indexToRemove));
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <ModalComponent onClose={() => setIsModalOpen(false)} title="COPIAR TURNOS A">
          <div className="flex flex-col gap-3 p-4">
            <div className="mb-2">
              <p className="font-medium mb-1">Seleccionar todos</p>
              <Checkbox
                name="select_all"
                isChecked={selectedDays.length === weekDays.length}
                onChange={(checked) => {
                  if (checked) {
                    setSelectedDays(weekDays.map((d) => d.value));
                  } else {
                    setSelectedDays([]);
                  }
                }}
              />
            </div>
            {weekDays.map((day) => (
              <div key={day.value} className="flex items-center justify-between">
                <span>{day.label}</span>
                <Checkbox
                  name="days"
                  isChecked={selectedDays.includes(day.value)}
                  onChange={(checked) =>
                    setSelectedDays((prev) => (checked ? [...prev, day.value] : prev.filter((d) => d !== day.value)))
                  }
                />
              </div>
            ))}
            <div className="flex justify-between mt-4 gap-4">
              <Button.Secondary text="Cancelar" onClick={() => setIsModalOpen(false)} />
              <Button.Primary text="Guardar" onClick={handleCopySchedule} />
            </div>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default ScheduleManager;
